import { useState, useEffect } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'

interface ViewColumn {
  name: string
  type: string
  nullable: boolean
  defaultValue?: string
}

interface ViewDetails {
  name: string
  schema?: string
  isUpdatable?: boolean
  columns?: ViewColumn[]
  comment?: string
  definition?: string
}

interface EditViewPanelProps {
  connection: DatabaseConnection
  database: string
  viewName: string
  schema?: string
  initialView?: ViewDetails
  onClose: () => void
  onSuccess: () => void
}

export function EditViewPanel({ 
  connection, 
  database, 
  viewName, 
  schema, 
  initialView,
  onClose,
  onSuccess 
}: EditViewPanelProps) {
  // For MySQL, don't use 'public' as schema - MySQL doesn't have schemas separate from databases
  const getSchemaValue = () => {
    if (connection.type === 'mysql') {
      // MySQL uses database name as schema, don't default to 'public'
      return initialView?.schema || schema || undefined
    }
    // PostgreSQL can use 'public' as default schema
    return initialView?.schema || schema || 'public'
  }
  
  const [formData, setFormData] = useState({
    name: initialView?.name || viewName,
    definition: initialView?.definition || '',
    schema: getSchemaValue()
  })
  const [isSaving, setIsSaving] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    if (initialView) {
      const schemaValue = connection.type === 'mysql' 
        ? (initialView.schema || schema || undefined)
        : (initialView.schema || schema || 'public')
      
      setFormData({
        name: initialView.name || viewName,
        definition: initialView.definition || '',
        schema: schemaValue
      })
    }
  }, [initialView, viewName, schema, connection.type])

  const handleSave = async () => {
    if (!formData.definition.trim()) {
      showToast('error', 'View definition is required')
      return
    }

    try {
      setIsSaving(true)
      // For MySQL, pass undefined for schema to avoid using 'public'
      const schemaToPass = connection.type === 'mysql' && formData.schema === undefined 
        ? undefined 
        : formData.schema
        
      await AgentProxyService.alterView(
        connection.id,
        database,
        viewName,
        {
          view_name: formData.name,
          definition: formData.definition,
          ...(schemaToPass !== undefined && { schema_name: schemaToPass })
        },
        connection.type === 'mysql' ? undefined : schema
      )
      
      showToast('success', 'View updated successfully')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to update view:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', `Failed to update view: ${message}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 w-1/2 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="text-2xl mr-3">✏️</div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit View
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Modify the view definition and properties
            </p>
          </div>
        </div>
        <button type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* View Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              View Name
            </label>
            <input
              type="text"
              value={formData.name}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
              placeholder="View name cannot be changed"
            />
          </div>

          {/* Schema - Only show for PostgreSQL */}
          {connection.type === 'postgresql' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Schema
              </label>
              <input
                type="text"
                value={formData.schema || 'public'}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                placeholder="Schema cannot be changed"
              />
            </div>
          )}

          {/* View Definition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              View Definition (SQL)
            </label>
            <textarea
              value={formData.definition}
              onChange={(e) => { setFormData({ ...formData, definition: e.target.value }) }}
              rows={15}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              placeholder="SELECT * FROM table_name WHERE condition"
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Enter the SQL SELECT statement that defines this view. Do not include CREATE VIEW statement.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button type="button"
          onClick={onClose}
          disabled={isSaving}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !formData.definition}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  )
}